import type { Celda, DocumentoExportable, Seccion } from "@/componentes/exportar/modelo"
import { comoFecha } from "@/lib/fechas"

import { ESTADO, type Evaluacion } from "./tipos"

/**
 * Una evaluación del desempeño como reporte (29/10): indicadores, consolidado por
 * bloque con su nota final, valoración, PDI y observaciones del trabajador.
 *
 * ⚠ **Se exporta lo GUARDADO, no el borrador de la pantalla**: un PDF que se firma o se
 * archiva tiene que decir lo que dice el sistema, y no lo que alguien estaba tecleando
 * sin guardar.
 *
 * ⚠ **La autoevaluación escrita NO va**: en pantalla está tapada hasta que la Dirección
 * pide verla, y un PDF no puede saltarse esa decisión. El promedio «Auto» de cada bloque
 * sí, porque el consolidado de la pantalla también lo enseña.
 */

const num = (v: string | null | undefined): number | null =>
  v === null || v === undefined || v === "" ? null : Number(v)

const VALORACION = [
  ["fortalezas", "Fortalezas reconocidas"],
  ["mejoras", "Áreas de mejora prioritarias"],
  ["brechas", "Bloques con brecha mayor que 1,0: casos revisados y conclusión"],
] as const

export function documentoDeEvaluacion(ev: Evaluacion): DocumentoExportable {
  const secciones: Seccion[] = []

  if (ev.plantilla.indicadores.length > 0) {
    secciones.push({
      titulo: "Parte 1 · Indicadores del período",
      columnas: [
        { titulo: "Indicador", tipo: "texto" },
        { titulo: "Fuente", tipo: "texto" },
        { titulo: "Resultado", tipo: "texto" },
        { titulo: "Comentario", tipo: "texto" },
      ],
      filas: ev.plantilla.indicadores.map((ind, i) => {
        const v = ev.indicadores[String(i)] ?? {}
        return { celdas: [ind.texto, ind.fuente, v.resultado || null, v.comentario || null] }
      }),
    })
  }

  const r = ev.resultado
  if (r) {
    const pie: Celda[][] = [
      [
        "Nota final",
        null,
        null,
        null,
        null,
        r.nivel
          ? `${r.nivel} — ${r.consecuencia ?? ""}`
          : `Faltan los bloques ${r.faltan.join(", ")}`,
        num(r.final),
      ],
    ]
    secciones.push({
      titulo: "Parte 3 · Consolidado por bloque",
      columnas: [
        { titulo: "Bloque", tipo: "texto" },
        { titulo: "Peso (%)", tipo: "numero" },
        { titulo: "Auto", tipo: "monto" },
        { titulo: "Dirección", tipo: "monto" },
        { titulo: "Brecha", tipo: "monto" },
        { titulo: "Acordada", tipo: "monto" },
        { titulo: "× peso", tipo: "monto" },
      ],
      filas: r.bloques.map((b) => ({
        celdas: [
          `${b.clave}. ${b.nombre}${b.revisar ? " (revisar con evidencias)" : ""}`,
          Math.round(Number(b.peso) * 100),
          num(b.auto),
          num(b.direccion),
          num(b.brecha),
          num(b.acordada),
          num(b.ponderada),
        ],
      })),
      pie,
    })
  }

  const valoracion = VALORACION.filter(([k]) => ev.valoracion[k])
  if (valoracion.length > 0) {
    secciones.push({
      titulo: "Parte 4 · Valoración de la Dirección",
      columnas: [
        { titulo: "Aspecto", tipo: "texto" },
        { titulo: "Valoración", tipo: "texto" },
      ],
      filas: valoracion.map(([k, t]) => ({ celdas: [t, ev.valoracion[k] ?? null] })),
    })
  }

  const trabajador = ev.pdi.trabajador ?? []
  if (trabajador.length > 0) {
    secciones.push({
      titulo: "Parte 5 · PDI — Compromisos del trabajador",
      columnas: [
        { titulo: "Qué va a mejorar", tipo: "texto" },
        { titulo: "Acción concreta", tipo: "texto" },
        { titulo: "Cómo se medirá", tipo: "texto" },
        { titulo: "Fecha límite", tipo: "fecha" },
      ],
      filas: trabajador.map((c) => ({
        celdas: [c.que || null, c.accion || null, c.medida || null, c.fecha || null],
      })),
    })
  }

  const empresa = ev.pdi.empresa ?? []
  if (empresa.length > 0) {
    secciones.push({
      titulo: "Parte 5 · PDI — Compromisos de la empresa",
      columnas: [
        { titulo: "Compromiso", tipo: "texto" },
        { titulo: "Responsable", tipo: "texto" },
        { titulo: "Fecha", tipo: "fecha" },
      ],
      filas: empresa.map((c) => ({
        celdas: [c.compromiso || null, c.responsable || null, c.fecha || null],
      })),
    })
  }

  const cierre: [string, string | null][] = [
    ["Decisión de la Dirección", ev.pdi.decision || null],
    ["Revisión de seguimiento del PDI", ev.pdi.seguimiento ? comoFecha(ev.pdi.seguimiento) : null],
    ["Observaciones del trabajador", ev.observaciones_trabajador],
    ["Cerrada el", ev.cerrada_en ? comoFecha(ev.cerrada_en) : null],
  ]
  const conDato = cierre.filter(([, v]) => v)
  if (conDato.length > 0) {
    secciones.push({
      titulo: "Decisión y cierre",
      columnas: [
        { titulo: "Concepto", tipo: "texto" },
        { titulo: "Valor", tipo: "texto" },
      ],
      filas: conDato.map(([t, v]) => ({ celdas: [t, v] })),
    })
  }

  return {
    titulo: `Evaluación del desempeño · ${ev.persona}`,
    subtitulo: [
      ev.plantilla.nombre,
      `${comoFecha(ev.desde)} al ${comoFecha(ev.hasta)}`,
      ESTADO[ev.estado],
    ].join(" · "),
    secciones,
  }
}
